<template>
    <div class="okta-login">
        <div class="okta-login__title">{{ title }}</div>
        <div class="okta-login__container">
            <div class="okta-login__item">
                <div class="okta-login__row">
                    <div class="okta-login__text">{{ textForLink }}</div>
                    <div class="okta-login__text okta-login__text_bold" :class="oktaLoginTextClasses">
                        {{ link }}
                    </div>
                </div>
                <div class="okta-login__row">
                    <div class="okta-login__text">{{ textForCode }}</div>
                    <div class="okta-login__text okta-login__text_bold" :class="oktaLoginTextClasses">
                        {{ userCode }}
                    </div>
                </div>
                <div class="okta-login__row">
                    <div class="okta-login__text">{{ textForTimer }}</div>
                    <div class="okta-login__text okta-login__text_bold">
                        {{ filterTime }}
                    </div>
                </div>
                <button
                    id="oktaFizzRefreshBtn"
                    ref="refreshBtnID"
                    role="button"
                    class="btn okta-login__btnRefresh"
                    :disabled="!isEnabledRefresh"
                    @click.prevent="fetchLink"
                >
                    {{ refreshBtnText }}
                </button>
            </div>
            <div class="okta-login__delimiter">
                <div class="okta-login__line" />
                <div class="delimiter__text">
                    {{ textOR }}
                </div>
                <div class="okta-login__line" />
            </div>
            <div class="okta-login__item" :class="oktaLoginItemClasses">
                <div class="okta-login__text">{{ textForScan }}</div>
                <qrcode-vue v-if="linkQrCode" class="qr-code-medium okta-login__qr-code" :value="linkQrCode" :level="'H'" render-as="svg"/>
            </div>
        </div>
        <div class="okta-login__title">{{ footnote }}</div>
    </div>
</template>

<script setup lang="ts">
    import QrcodeVue from 'qrcode.vue';
    import { Strings } from "@/locales/types/enums/strings";
    import { type OktaToken, type AppError } from "@ca.vmedia.tv/domains";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { useChannelsStore } from "@/stores/Channels";
    import { useCleanUrl } from "@/composables/useCleanUrl";
    import { useI18n } from "vue-i18n";
    import { useLanguagesUtils } from "@/composables/useLanguagesUtils";
    import { computed, nextTick, onBeforeUnmount, onMounted, watch, ref, type Ref } from "vue";
    import { useRouter } from "vue-router";
    import { useAccountStore } from "@/stores/Account";
    import { storeToRefs } from 'pinia'
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { useUserSettingsStore } from "@/stores/UserSettings";
    import { useDeviceSettingsStore } from "@/stores/DeviceSettings";
    import { useClientMessagesStore } from "@/stores/ClientMessages";
    import { UrlPathUtils } from '@/utils';

    const { getChannels } = useChannelsStore();
    
    const ClientMessagesStore = useClientMessagesStore();
    const {
        startReceiving
    } = ClientMessagesStore;

    const AccountStoreModule = useAccountStore();
    const {
        FetchOktaDevice,
        FetchOktaToken,
        OktaLogin,
    } = AccountStoreModule;
    const {
        getOktaDevice: oktaDevice,
        getOktaToken: oktaToken
    } = storeToRefs(AccountStoreModule);

    const UserInfoStoreModule = useUserInfoStore();
    const {
        fetchSettings,
    } = useUserSettingsStore();
    const { setupSettings } = useDeviceSettingsStore();
    const {
        isClient,
    } = storeToRefs(UserInfoStoreModule);

    const { mainPage } = useAppConfig();
    const { t } = useI18n();
    const { $navigator } = useNuxtApp();
    const router = useRouter();
    const { adjustLanguageSettings } = useLanguagesUtils();
    const refreshBtnText: string = t(Strings.LOGIN_OKTA_FIZZ_BUTTON).toString();
    const title: string = t(Strings.LOGIN_OKTA_FIZZ_TITLE).toString();
    const textForLink: string = t(Strings.LOGIN_OKTA_FIZZ_GO_TO_WEBSITE).toString();
    const textForCode: string = t(Strings.LOGIN_OKTA_FIZZ_AUTH_CODE).toString();
    const textForTimer: string = t(Strings.LOGIN_OKTA_FIZZ_AUTH_TIMER).toString();
    const textOR: string = t(Strings.LOGIN_OKTA_FIZZ_OR).toString();
    const textForScan: string = t(Strings.LOGIN_OKTA_FIZZ_SCAN).toString();
    const footnote: string = t(Strings.LOGIN_OKTA_FIZZ_FOOTNOTE).toString();
    const refreshBtnID: Ref<HTMLButtonElement | null> = ref(null);
    const timerFetchToken: Ref<ReturnType<typeof setInterval> | null> = ref(null);
    const time: Ref<number> = ref(-1);
    const timer: Ref<ReturnType<typeof setInterval> | null> = ref(null);
    const isRunning: Ref<boolean> = ref(false);
    const isRegKeyListeners: Ref<boolean> = ref(false);
 
    const isEnabledRefresh = computed((): boolean => time.value == 0);
    const oktaLoginTextClasses = computed((): {[key: string]: boolean} => {
        return {
            'okta-login__text_skeleton': !linkQrCode.value
        }
    });
    const oktaLoginItemClasses = computed((): {[key: string]: boolean} => {
        return {
            'okta-login__item_skeleton': !linkQrCode.value
        }
    });
    const linkQrCode = computed((): string => {
        if (oktaDevice.value) {
            return oktaDevice.value.verificationUriComplete!;
        }
        return "";
    });
    const link = computed((): string => {
        if (oktaDevice.value) {
            return useCleanUrl(oktaDevice.value.verificationUri!);
        }
        return "";
    });
    const userCode = computed((): string => {
        if (oktaDevice.value) {
            return oktaDevice.value.userCode!;
        }
        return "";
    });
    const filterTime = computed((): string => {
        const data = prettyTime.value.split(':');
        let minutes = data[0];
        let secondes = data[1];
        if (Number(minutes)  < 10) {
            minutes = "0"+minutes;
        }
        if (Number(secondes) < 10) {
            secondes = "0"+secondes;
        }
        return minutes+":"+secondes;
    });
    const prettyTime = computed((): string => {
        const notPrettyTime = time.value < 0 ? 0 : time.value / 60;
        const minutes = Math.floor(notPrettyTime);
        const secondes = Math.round((notPrettyTime - minutes) * 60);
        
        return minutes+":"+secondes;
    });


    const focusButton = (): void => {
        if (refreshBtnID.value) {
            refreshBtnID.value?.focus();
        }
    };
    const registerKeyListeners = () => {
        if (!isRegKeyListeners.value) {
            $navigator.on("keypress:back", keyBackListener);
            isRegKeyListeners.value = true;
        }
    };
    const unregisterKeyListeners = () => {
        if (isRegKeyListeners.value) {
            $navigator.off("keypress:back", keyBackListener);
            isRegKeyListeners.value = false;
        }
    };
    const keyBackListener = () => {
        router.go(-1);
    };
    const fetchLink = async () => {
        try {
            if (timerFetchToken.value) {
                clearInterval(timerFetchToken.value);
            }
            await FetchOktaDevice();

            time.value = Number(oktaDevice.value?.expiresIn);

            start();
            timerFetchToken.value = setInterval(() => {
                fetchToken();
            }, Number(oktaDevice.value?.interval) * 1000);
        } catch (error) {
            Logger.captureError(error as Error);
        }
    };
    const fetchToken = () => {
        if (oktaDevice.value) {
            FetchOktaToken(oktaDevice.value.deviceCode!).catch((error) => {
                Logger.captureError(error as Error);
            });
        }
    };
    const start = () => {
        isRunning.value = true;
        if (!timer.value) {
            timer.value = setInterval(() => {
            time.value--;
        }, 1000);
        }
    };
    const stop = () => {
        isRunning.value = false;
        clearInterval(timer.value!);
        timer.value = null;
    };
    const reset = () => {
        stop();
        time.value = 0;
    };
    

    watch(
        time,
        (val: number) => {
            if (val === 0) {
                reset();
            }
        }
    );
    watch(
        isEnabledRefresh,
        (val: boolean) => {
           void nextTick(() => {
                if (val) {
                    focusButton();
                }
            });
        }
    );
    watch(
        oktaToken,
        async (value: OktaToken | null) => {
            if (value) {
                clearInterval(timerFetchToken.value!);
                await OktaLogin(value.accessToken).catch((error: AppError) => {
                    void router.push(UrlPathUtils.buildUrl(linkItems.error.routerPath, {errorCode: error.code, errorMessage: error.message}));
                });
            }
        }
    );
    watch(
        isClient,
        async (value: boolean) => {
            if (value) {
                await fetchSettings();
                // load channels
                await getChannels();
                
                startReceiving().catch((error) => {
                    Logger.captureError(error as Error);
                });
                await setupSettings();
                await adjustLanguageSettings();
                void router.push(linkItems[mainPage].routerPath);
            } 
        }
    );

    onBeforeUnmount(() => {
        unregisterKeyListeners();
        clearInterval(timerFetchToken.value!);
    });

    onMounted(() => {
        void fetchLink();
        void nextTick((): void => {
            registerKeyListeners();
        });
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>